import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { Subject } from 'rxjs';
import { IChecklistV2, ICheckpointGroup, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { cloneDeep } from '@fi-shared/utils';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';
import { IAIChecklist2Model } from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiSectionsHelperService } from '@fi-shared/fi-sections-dnd-editor/services/fi-sections-helper.service';

@Component({
    selector: 'fi-checklist2-checkpoints-section-edit',
    templateUrl: 'checkpoints-section-edit.component.html',
    styleUrls: ['./checkpoints-section-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'checkpoints-section-edit' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiChecklist2CheckpointsSectionEditComponent }],
    standalone: false,
})
export class FiChecklist2CheckpointsSectionEditComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() checklistId: number;

    @HostBinding('class.actions-offset-bottom') @Input() isChatAvailable: boolean;
    private checklist: IChecklistV2;

    protected checkpointGroups: ICheckpointGroup[] = [];
    private fiValuesComparison: FiValuesComparison<ICheckpointGroup[]>;

    private isQRCodeEnabled: boolean;
    protected isComplianceThresholdEnabled: boolean;

    protected getNewCheckpointGroupInstance = (): ICheckpointGroup => {
        return this.fiChecklistHelperService.getCheckpointGroupInstanceV2();
    };

    protected get selectedLanguageId(): number {
        return this.fiChecklistStoreService.selectedLanguageId;
    }

    constructor(
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private fiSectionsHelperService: FiSectionsHelperService,
        private cdr: ChangeDetectorRef,
        private saveChangesWarningService: SaveChangesWarningService,
        private userDataService: FiUserDataService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.setTranslationData();
        this.setCheckpointGroups();
    }

    protected onCheckpointGroupsChange(checkpointGroups: ICheckpointGroup[]) {
        this.checkpointGroups = cloneDeep(checkpointGroups);
        this.fiChecklistEditorStoreService.setCurrentCheckpointGroups(this.checkpointGroups);
        this.cdr.markForCheck();
    }

    protected isAIAssistanceEnabled(): boolean {
        return this.fiChecklistHelperService.isAIAssistanceEnabled();
    }

    protected addCheckpointsFromAI(aiChecklist2: IAIChecklist2Model): void {
        this.fiChecklistHelperService.mergeCheckpointsWithAIResponse(
            this.checkpointGroups,
            aiChecklist2,
            this.fiChecklistStoreService.selectedLanguageId,
        );
        this.onCheckpointGroupsChange(this.checkpointGroups);
    }

    private setTranslationData(): void {
        if (!this.fiChecklistStoreService.availableContentLanguages?.length) {
            this.fiChecklistStoreService.selectedLanguageId = this.userDataService.userData.PreferredContentLanguage;
            this.fiChecklistStoreService.availableContentLanguages =
                this.fiContentLanguageSharedService.getContentLanguages();
        }
    }

    private readonly checkpointGroupsWatcherIndex: number = 0;
    private readonly checklistWatcherIndex: number = 1;

    private setSaveChangeWarningWatcher(): void {
        const objectsFn = new Array(2);
        objectsFn[this.checkpointGroupsWatcherIndex] = () => this.checkpointGroups;
        objectsFn[this.checklistWatcherIndex] = () => this.checklist;

        this.saveChangesWarningService.startForMultipleObjects(objectsFn);
        this.fiValuesComparison = new FiValuesComparison(() => this.checkpointGroups);
        this.saveChangesWarningService.preventParentStateObjCheck = true;
        this.saveChangesWarningService.parentStateObjIndexes = [this.checklistWatcherIndex];
    }

    private setCheckpointGroups(): void {
        this.checklist = cloneDeep(this.fiChecklistEditorStoreService.getInitialChecklistData());
        const currentChecklistData: IChecklistV2 = this.fiChecklistEditorStoreService.getCurrentChecklistData$().value;
        this.checkpointGroups = this.initGroupNamesInGroups(currentChecklistData.CheckpointGroups);

        this.setSaveChangeWarningWatcher();

        this.checkpointGroups = this.initGroupNamesInGroups(
            this.fiChecklistEditorStoreService.getCurrentCheckpointGroups(),
        );
        this.checklist = cloneDeep(currentChecklistData);

        this.isQRCodeEnabled = currentChecklistData.IsQRCodeEnabled;
        this.isComplianceThresholdEnabled = currentChecklistData.IsComplianceThresholdEnabled;
    }

    private initGroupNamesInGroups(checkpointGroups: ICheckpointGroup[]): ICheckpointGroup[] {
        const newCheckpointGroups: ICheckpointGroup[] = cloneDeep(checkpointGroups);
        return newCheckpointGroups.map((checkpointGroup: ICheckpointGroup) => {
            return this.getCheckpointGroupWithDefaultName(checkpointGroup);
        });
    }

    protected onBackButtonClick(): void {
        if (!this.fiValuesComparison.isChanged()) {
            this.saveChangesWarningService.stop();
        }

        window.history.back();
    }

    protected addNewCheckpoint(checkpointGroupIndex: number = 0): void {
        this.fiChecklistHelperService
            .getAddNewCheckpointDialog()
            .onYesButtonClick((checkpoint: ICheckpointV2) => {
                this.checkpointGroups[checkpointGroupIndex].Checkpoints.push(checkpoint);
                this.fiChecklistEditorStoreService.setCurrentCheckpointGroups(this.checkpointGroups);
                const checkpointIndex: number = this.checkpointGroups[checkpointGroupIndex].Checkpoints.length - 1;
                this.saveChangesWarningService.stop();
                this.fiChecklistHelperService.navigateToEditCheckpoint(
                    checkpointGroupIndex,
                    checkpointIndex,
                    this.checklistId,
                    this.accessRightFilterCollection,
                );
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected showCheckpointEditor(checkpointIndex: number, checkpointGroupIndex: number): void {
        this.saveChangesWarningService.stop();
        this.fiChecklistHelperService.navigateToEditCheckpoint(
            checkpointGroupIndex,
            checkpointIndex,
            this.checklistId,
            this.accessRightFilterCollection,
        );
    }

    protected openCheckpointGroupDeleteConfirmationDialog(checkpointGroupIndex: number): void {
        this.fiDialogsService
            .confirmSimpleDelete({
                headerText: this.appTextService.get('checklist.checkpoint_group_delete'),
                messageText: this.appTextService.get('checklist.checkpoint_group_delete_confirm_message'),
                preventUppercaseButtonLabel: true,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-confirm-delete-dialog-panel'],
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => this.removeChecklistGroup(checkpointGroupIndex));
    }

    private removeChecklistGroup(checkpointGroupIndex: number): void {
        this.checkpointGroups.splice(checkpointGroupIndex, 1);
        this.fiChecklistEditorStoreService.setCurrentCheckpointGroups(this.checkpointGroups);
        this.cdr.markForCheck();
    }

    protected isThreeColumnTemplate(): boolean {
        return this.isTranslationCardVisible() || this.isAIAssistanceEnabled();
    }

    protected isTranslationCardVisible(): boolean {
        return this.fiChecklistHelperService.isTranslationCardVisible();
    }

    protected saveCheckpointGroups(): void {
        this.fiChecklistEditorStoreService.updateCheckpointGroupsOnChecklist(this.checkpointGroups);
        this.saveChangesWarningService.stop();
        window.history.back();
    }

    private getCheckpointGroupWithDefaultName(checkpointGroup: ICheckpointGroup): ICheckpointGroup {
        return this.fiSectionsHelperService.setDefaultSectionName(
            checkpointGroup,
            this.selectedLanguageId,
            ObjectTypes.CheckpointGroup,
        );
    }

    protected ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly ObjectTypes = ObjectTypes;
}
